import { formatCurrency } from "../../../format";
import type {
  MarketFunnelRejectionReason,
  MarketFunnelSnapshot,
  MarketFunnelStage
} from "../domain/types";

const rejectionLabels: Record<MarketFunnelRejectionReason, string> = {
  area_difference: "Rozdielna výmera",
  different_condition: "Materiálne odlišný stav",
  insufficient_locality: "Nedostatočné údaje o lokalite",
  low_confidence: "Nízka dátová dôvera",
  low_similarity: "Nízka podobnosť",
  low_selection_score: "Nízke selection score",
  inactive_or_invalid: "Neaktívne alebo neplatné",
  duplicate: "Duplicity",
  other: "Iný dôvod"
};

const stageRows = (snapshot: MarketFunnelSnapshot): Array<[string, MarketFunnelStage]> => [
  ["Celý trh", snapshot.stages.allMarket],
  ["Podobná výmera", snapshot.stages.similarArea],
  ["Podobný stav", snapshot.stages.similarCondition],
  ["Kvalitné porovnania", snapshot.stages.primaryComparables],
  ["Konkurencia", snapshot.stages.marketCompetition]
];

const PriceDistribution = ({ label, stage }: { label: string; stage: MarketFunnelStage }) => {
  const distribution = stage.priceDistribution;
  if (!distribution) return null;
  return (
    <li>
      <strong>{label}<small>N = {distribution.count}{stage.subjectPricePercentile !== undefined ? ` · naša cena P${stage.subjectPricePercentile}` : ""}</small></strong>
      <span><small>MIN</small>{formatCurrency(distribution.min)}</span>
      <span><small>Q1</small>{formatCurrency(distribution.q1)}</span>
      <span><small>MEDIÁN</small>{formatCurrency(distribution.median)}</span>
      <span><small>Q3</small>{formatCurrency(distribution.q3)}</span>
      <span><small>MAX</small>{formatCurrency(distribution.max)}</span>
    </li>
  );
};

type MarketFunnelContextProps = {
  snapshot?: MarketFunnelSnapshot;
  resultMode?: boolean;
};

export function MarketFunnelContext({ snapshot, resultMode = false }: MarketFunnelContextProps) {
  if (!snapshot) return null;
  const similarAreaMedian = snapshot.stages.similarArea.priceDistribution?.median;
  const rejectionEntries = Object.entries(snapshot.rejectionReasonCounts)
    .filter(([, count]) => count > 0) as Array<[MarketFunnelRejectionReason, number]>;

  return (
    <section className={`market-funnel ${resultMode ? "result" : ""}`} aria-label="Trhový kontext">
      <header>
        <div><span>TRHOVÝ KONTEXT</span><strong>{snapshot.marketLabel}</strong></div>
        <time dateTime={snapshot.capturedAt}>{new Intl.DateTimeFormat("sk-SK", { dateStyle: "short" }).format(new Date(snapshot.capturedAt))}</time>
      </header>

      <div className="market-funnel-metrics">
        <span>Trh<strong>{snapshot.stages.allMarket.count}</strong></span>
        <span>Podobná výmera<strong>{snapshot.stages.similarArea.count}</strong></span>
        <span>Podobný stav<strong>{snapshot.conditionStageAvailable ? snapshot.stages.similarCondition.count : "–"}</strong></span>
        <span>Kvalitné porovnania<strong>{snapshot.stages.primaryComparables.count}</strong></span>
        <span>Konkurencia<strong>{snapshot.stages.marketCompetition.count}</strong></span>
      </div>

      {snapshot.areaBand ? (
        <p className="market-funnel-method">
          Podobná výmera: {snapshot.areaBand.min.toLocaleString("sk-SK")}–{snapshot.areaBand.max.toLocaleString("sk-SK")} m²
          {snapshot.subjectArea ? ` pri porovnávanej ploche ${snapshot.subjectArea.toLocaleString("sk-SK")} m²` : ""}.
        </p>
      ) : null}

      {resultMode ? (
        <div className="market-funnel-result-summary">
          <span>Naša cena<strong>{snapshot.subjectAskingPrice ? formatCurrency(snapshot.subjectAskingPrice) : "Neuvedená"}</strong></span>
          <span>Medián podobnej výmery<strong>{similarAreaMedian ? formatCurrency(similarAreaMedian) : "Nedostupný"}</strong></span>
        </div>
      ) : null}

      <div className="market-funnel-position">
        {snapshot.stages.similarArea.subjectPositionText
          ?? snapshot.stages.allMarket.subjectPositionText
          ?? "Cenovú pozíciu nemožno určiť bez ceny subjectu a ponukových cien."}
      </div>

      <details className="market-funnel-distribution">
        <summary>Cenové rozpätie trhu</summary>
        <ul>{stageRows(snapshot).map(([label, item]) => <PriceDistribution key={label} label={label} stage={item} />)}</ul>
      </details>

      <details className="market-funnel-diagnostics">
        <summary>Diagnostika výberu</summary>
        <p>Hard-eligible po zhode obce, typu a izieb: <strong>{snapshot.hardEligibleCount}</strong></p>
        {rejectionEntries.length > 0 ? (
          <ul>
            {rejectionEntries.map(([reason, count]) => (
              <li key={reason}>
                <strong>{rejectionLabels[reason]} <b>{count}</b></strong>
                {(snapshot.rejectionSamples[reason] ?? []).map((sample) => (
                  <small key={sample.id}>{sample.title}{sample.area ? ` · ${sample.area} m²` : ""}{sample.askingPrice ? ` · ${formatCurrency(sample.askingPrice)}` : ""}</small>
                ))}
              </li>
            ))}
          </ul>
        ) : <p>Bez vyradených hard-eligible ponúk.</p>}
        <div className="market-funnel-cheaper">
          <strong>Lacnejšie ponuky s podobnou výmerou</strong>
          {snapshot.cheaperSimilarAreaRejected.length > 0
            ? snapshot.cheaperSimilarAreaRejected.map((sample) => (
              <small key={sample.id}>{sample.title} · {sample.askingPrice ? formatCurrency(sample.askingPrice) : "bez ceny"} · {rejectionLabels[sample.reason]}</small>
            ))
            : <small>Importovaný market pool neobsahuje lacnejšiu ponuku s podobnou výmerou vyradenú z kvalitných porovnaní.</small>}
        </div>
      </details>
    </section>
  );
}
